import { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import {Mail, Phone, MessageSquare, Search, Archive, CheckCircle, Clock, ExternalLink} from 'lucide-react'

interface Contact {
  _id: string
  name: string
  email: string
  phone?: string
  subject: string
  message: string
  status: 'PENDING' | 'RESPONDED' | 'ARCHIVED'
  creator: string
  createdAt: string
  updatedAt: string
}

export default function ContactsManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'PENDING' | 'RESPONDED' | 'ARCHIVED'>('PENDING')
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)

  const { data: contacts, loading, fetchData, updateRecord } = useEntityCRUD<Contact>('contacts')
  const { logAction } = useAdminLog()

  useEffect(() => {
    fetchData()
    // Recarregar a cada 30 segundos para capturar novos contatos
    const interval = setInterval(() => {
      fetchData()
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'ALL' || contact.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const handleUpdateStatus = async (contact: Contact, newStatus: 'PENDING' | 'RESPONDED' | 'ARCHIVED') => {
    try {
      await updateRecord(contact._id, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      await logAction('UPDATE', 'contacts', contact._id, `Status atualizado: ${newStatus}`)
      if (selectedContact?._id === contact._id) {
        setSelectedContact(null)
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      alert('Erro ao atualizar status')
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pendente' },
      RESPONDED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Respondido' },
      ARCHIVED: { color: 'bg-gray-100 text-gray-800', icon: Archive, label: 'Arquivado' }
    }
    const badge = badges[status as keyof typeof badges]
    const Icon = badge.icon
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    )
  }

  const openWhatsApp = (phone?: string, name?: string) => {
    if (!phone) return
    const cleanPhone = phone.replace(/\D/g, '')
    const message = `Olá ${name}, vim do REFUGE IN CHRIST MINISTRY...`
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank')
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contatos Recebidos</h1>
            <p className="text-gray-600 mt-1">Gerenciar mensagens de contato pastoral</p>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por nome, email ou assunto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              {['ALL', 'PENDING', 'RESPONDED', 'ARCHIVED'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status as any)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    filterStatus === status ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status === 'ALL' ? 'Todos' : status === 'PENDING' ? 'Pendentes' : status === 'RESPONDED' ? 'Respondidos' : 'Arquivados'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Contacts List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Contact List */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Mensagens ({filteredContacts.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : filteredContacts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">Nenhum contato encontrado</div>
            ) : (
              <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact._id}
                    onClick={() => setSelectedContact(contact)}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedContact?._id === contact._id ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 line-clamp-1">{contact.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-1">{contact.email}</p>
                      </div>
                      {getStatusBadge(contact.status)}
                    </div>
                    <p className="text-sm font-medium text-gray-700 line-clamp-1">{contact.subject}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(contact.createdAt).toLocaleDateString('pt-BR')} às{' '}
                      {new Date(contact.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Contact Details */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md overflow-hidden">
            {selectedContact ? (
              <div>
                <div className="p-6 bg-gray-50 border-b border-gray-200">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedContact.name}</h2>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail size={16} />
                          <a href={`mailto:${selectedContact.email}`} className="hover:text-blue-600">
                            {selectedContact.email}
                          </a>
                        </div>
                        {selectedContact.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone size={16} />
                            {selectedContact.phone}
                          </div>
                        )}
                      </div>
                    </div>
                    {getStatusBadge(selectedContact.status)}
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Assunto:</p>
                    <p className="font-semibold text-gray-900">{selectedContact.subject}</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <MessageSquare size={16} />
                      Mensagem:
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-800 whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`mailto:${selectedContact.email}`}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Mail size={18} />
                      Responder por Email
                      <ExternalLink size={16} />
                    </a>
                    {selectedContact.phone && (
                      <button
                        onClick={() => openWhatsApp(selectedContact.phone, selectedContact.name)}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Phone size={18} />
                        WhatsApp
                        <ExternalLink size={16} />
                      </button>
                    )}
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Atualizar Status:</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedContact.status !== 'PENDING' && (
                        <button
                          onClick={() => handleUpdateStatus(selectedContact, 'PENDING')}
                          className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
                        >
                          <Clock size={16} />
                          Marcar como Pendente
                        </button>
                      )}
                      {selectedContact.status !== 'RESPONDED' && (
                        <button
                          onClick={() => handleUpdateStatus(selectedContact, 'RESPONDED')}
                          className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-lg hover:bg-green-200 transition-colors text-sm"
                        >
                          <CheckCircle size={16} />
                          Marcar como Respondido
                        </button>
                      )}
                      {selectedContact.status !== 'ARCHIVED' && (
                        <button
                          onClick={() => handleUpdateStatus(selectedContact, 'ARCHIVED')}
                          className="flex items-center gap-2 bg-gray-100 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                        >
                          <Archive size={16} />
                          Arquivar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center p-8 text-gray-500">
                <div className="text-center">
                  <MessageSquare className="mx-auto mb-4 text-gray-400" size={64} />
                  <p>Selecione um contato para ver os detalhes</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Contatos</p>
                <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
              </div>
              <MessageSquare className="text-blue-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {contacts.filter(c => c.status === 'PENDING').length}
                </p>
              </div>
              <Clock className="text-yellow-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Respondidos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {contacts.filter(c => c.status === 'RESPONDED').length}
                </p>
              </div>
              <CheckCircle className="text-green-600" size={32} />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
